<app-page-shell variant="default" [embedded]="embedded">
<div class="pm-page" [class.pm-page--embedded]="embedded">

  @if (error) {
  <div class="sh-alert sh-alert--error mb-3 pm-error">
    {{error}}
    <app-sb-button class="ms-2" variant="outline" size="sm" label="Try again" icon="fa fa-sync-alt" (clicked)="load()"></app-sb-button>
  </div>
  }
  @if (loading && !data) {
  <div class="sh-alert sh-alert--unknown mb-3 pm-loading">Loading monitoring…</div>
  }

  @if (data) {
    <!-- 1. State -->
    <div class="card card-box mb-4 pm-state">
      <div class="card-header py-3 pm-card-header">
        <div class="card-header--title font-size-lg">Widget monitoring</div>
        <div class="pm-card-header__actions">
          <app-sb-button variant="ghost" size="sm" icon="fa fa-sync-alt" label="Refresh" [loading]="loading" (clicked)="load()"></app-sb-button>
          @if (manage) {
          <app-sb-button class="pm-log-issue" data-action="log-issue" variant="outline-secondary" size="sm"
                         icon="fa fa-flag" label="Log an Issue" [disabled]="busy" (clicked)="logIssue()"></app-sb-button>
          }
          @if (manage && monitoringState !== 'disabled') {
          <app-sb-button class="pm-run-now" variant="primary" size="sm" icon="fa fa-play"
                         label="Run check now" [loading]="busy" [disabled]="busy || runNowBlocked"
                         (clicked)="runNow()"></app-sb-button>
          }
          @if (manage && !data.monitoring.disabled) {
          <app-sb-button class="pm-disable" data-action="disable" variant="outline-secondary" size="sm"
                         icon="fa fa-power-off" label="Disable monitoring" [disabled]="busy"
                         (clicked)="disableMonitoring()"></app-sb-button>
          }
        </div>
      </div>
      <div class="card-body">
        @if (data.monitoring.active) {
        <div class="pm-state__line pm-state--active">
          <span class="pm-dot pm-dot--ok" aria-hidden="true"></span>
          <div>
            <div class="pm-state__title">Monitoring is active</div>
            <div class="sh-muted">
              @if (data.monitoring.lastCheckedAt) {Last check {{time(data.monitoring.lastCheckedAt)}}} @else {No check has run yet}
              <ng-template #neverChecked>No check has run yet</ng-template>
              @if (data.monitoring.nextCheckAt) { (next {{time(data.monitoring.nextCheckAt)}})}
            </div>
          </div>
        </div>
        } @else if (monitoringState === 'disabled') {
          <div class="pm-state__line pm-state--disabled">
            <span class="pm-dot pm-dot--disabled" aria-hidden="true"></span>
            <div>
              <div class="pm-state__title">Monitoring is disabled</div>
              @if (disabledByText) {
              <div class="pm-disabled-by">{{disabledByText}}</div>
              }
              @if (data.monitoring.lastCheckedAt) {
              <div class="sh-muted">Last check {{time(data.monitoring.lastCheckedAt)}}</div>
              }
              @if (data.monitoring.reasons?.length) {
              <ul class="pm-reasons">
                @for (reason of data.monitoring.reasons; track reason) {
                <li [attr.data-reason]="reason.code"><span class="pm-reason__message">{{reason.message}}</span>@if (reason.hint) {
<div class="pm-reason__hint sh-muted">{{reason.hint}}</div>
}</li>
                }
              </ul>
              }
              <div class="sh-muted">Nothing is checked or emailed. The issues open when it was disabled were closed; the ones logged by hand stay open.</div>
              @if (manage && data.monitoring.disabled) {
              <app-sb-button class="d-inline-block mt-2 pm-enable" data-action="enable" variant="primary" size="sm"
                             icon="fa fa-power-off" label="Enable monitoring" [loading]="busy" [disabled]="busy"
                             (clicked)="enableMonitoring()"></app-sb-button>
              }
            </div>
          </div>
        } @else {
          <div class="pm-state__line pm-state--paused">
            <span class="pm-dot pm-dot--paused" aria-hidden="true"></span>
            <div>
              <div class="pm-state__title">Monitoring is paused</div>
              @if (data.monitoring.lastCheckedAt) {
              <div class="sh-muted">Last check {{time(data.monitoring.lastCheckedAt)}}</div>
              }
              @if (data.monitoring.reasons?.length) {
              <ul class="pm-reasons">
                @for (reason of data.monitoring.reasons; track reason) {
                <li [attr.data-reason]="reason.code"><span class="pm-reason__message">{{reason.message}}</span>@if (reason.hint) {
<div class="pm-reason__hint sh-muted">{{reason.hint}}</div>
}</li>
                }
              </ul>
              }
              <div class="sh-muted">Monitoring resumes by itself once every reason above is resolved. Issues found before the pause stay listed.</div>
            </div>
          </div>
        }
        <ng-template #pausedState>
          <div class="pm-state__line pm-state--paused">
            <span class="pm-dot pm-dot--paused" aria-hidden="true"></span>
            <div>
              <div class="pm-state__title">Monitoring is paused</div>
              @if (data.monitoring.lastCheckedAt) {
              <div class="sh-muted">Last check {{time(data.monitoring.lastCheckedAt)}}</div>
              }
              @if (data.monitoring.reasons?.length) {
              <ul class="pm-reasons">
                @for (reason of data.monitoring.reasons; track reason) {
                <li [attr.data-reason]="reason.code"><span class="pm-reason__message">{{reason.message}}</span>@if (reason.hint) {
<div class="pm-reason__hint sh-muted">{{reason.hint}}</div>
}</li>
                }
              </ul>
              }
              <div class="sh-muted">Monitoring resumes by itself once every reason above is resolved. Issues found before the pause stay listed.</div>
            </div>
          </div>
        </ng-template>
        @if (manage && runNowHint) {
        <div class="sh-muted mt-2 pm-run-hint">{{runNowHint}}</div>
        }
        <div class="sh-muted mt-2">Times are in {{timezone}}.</div>
      </div>
    </div>

    <!-- 2. Status -->
    <div class="card card-box mb-4 pm-status">
      <div class="card-header py-3">
        <div class="card-header--title font-size-lg">Status</div>
      </div>
      <div class="card-body pm-status__body">
        <span class="pm-status__badge" [class]="levelBadgeClass(data.status.level)">{{levelLabel(data.status.level)}}</span>
        @if (data.status.since) {
        <span class="sh-muted">since {{time(data.status.since)}}</span>
        }
      </div>
    </div>

    <!-- 3. Expected sites -->
    <div class="card card-box mb-4 pm-sites">
      <div class="card-header py-3">
        <div class="card-header--title font-size-lg">Expected sites</div>
      </div>
      <div class="card-body">
        <p class="sh-muted mb-3">The external sites the widget is expected to run on. Widget loads count as unexpected on a declined site, and on a new site seen only through widget loads that was found after the first check settled this list. A page that only mentions the widget's address does not count.</p>
        @if (pendingCount) {
        <div class="sh-alert sh-alert--warn mb-3 pm-pending-notice">
          {{pendingCount === 1 ? '1 site is' : pendingCount + ' sites are'}} awaiting your confirmation.
          A site proposed by a registration counts as expected until you decide. A site seen only through widget loads is marked below when its loads count as unexpected until you approve it.
        </div>
        }
        @if (!data.sites.length) {
        <div class="sh-muted pm-no-sites">No site found yet. The first registration through the widget on an external site proposes it here.</div>
        }
        @if (data.sites.length) {
        <div class="sh-table-wrap">
          <table class="sh-table pm-table">
            <thead>
            <tr>
              <th>Host</th>
              <th>Status</th>
              <th>First seen</th>
              <th class="pm-num">Registrations</th>
              <th class="pm-num">Loads (7 days)</th>
              <th>Sample page</th>
              <th>Decision</th>
              @if (manage) {
              <th></th>
              }
            </tr>
            </thead>
            <tbody>
            @for (site of data.sites; track trackById($index, site)) {
            <tr [attr.data-site-id]="site.id">
              <td class="pm-cell--wrap"><strong>{{site.host}}</strong>
                @if (site.status === 'pending') {
                  @if (siteExpected(site)) {
                  <div class="sh-muted pm-site-note">{{siteSourceText(site)}}. Awaiting your confirmation.</div>
                  } @else if (siteCountsAsUnexpected(site)) {
                  <div class="sh-muted pm-site-note pm-site-note--unexpected">Seen only through widget loads. Counts as an unexpected site until you approve it.</div>
                  } @else {
                  <div class="sh-muted pm-site-note">Seen only through widget loads. Awaiting your confirmation.</div>
                  }
                  @if (audienceText(site)) {
                  <div class="sh-muted pm-site-note pm-site-audience">{{audienceText(site)}}</div>
                  }
                }
              </td>
              <td><span [class]="siteChipClass(site.status)">{{site.status}}</span></td>
              <td>{{time(site.firstSeenAt)}}</td>
              <td class="pm-num">{{metricText(site.registrations)}}</td>
              <td class="pm-num">{{metricText(site.loads7d)}}</td>
              <td class="pm-cell--wrap pm-mono">{{site.samplePath || '—'}}</td>
              <td class="pm-cell--wrap">
                @if (site.decidedAt) { {{time(site.decidedAt)}}@if (site.decidedBy?.name) { by {{site.decidedBy.name}}} @if (site.autoReason) {
                  <div class="sh-muted pm-site-auto">Approved automatically@if (site.autoReasonText) {: {{site.autoReasonText}}}</div>
                  }
                  @if (site.note) {
                  <div class="sh-muted">{{site.note}}</div>
                  }
            } @else {
            <span class="sh-muted">—</span>
            }
                <ng-template #undecided><span class="sh-muted">—</span></ng-template>
              </td>
              @if (manage) {
              <td>
                <div class="pm-actions">
                  @if (site.status !== 'approved') {
                  <app-sb-button data-action="approve" variant="outline" size="sm" label="Approve"
                                 [disabled]="busy" (clicked)="approveSite(site)"></app-sb-button>
                  }
                  @if (site.status !== 'declined') {
                  <app-sb-button data-action="decline" variant="outline-danger" size="sm" label="Decline"
                                 [disabled]="busy" (clicked)="declineSite(site)"></app-sb-button>
                  }
                  @if (site.status !== 'declined') {
                  <app-sb-button data-action="replace" variant="outline-secondary" size="sm" label="Replace"
                                 [disabled]="busy" (clicked)="replaceSite(site)"></app-sb-button>
                  }
                </div>
              </td>
              }
            </tr>
        }
            </tbody>
          </table>
        </div>
  }
        @if (sitesNotShownText) {
        <div class="sh-muted mt-2 pm-sites-more">{{sitesNotShownText}}</div>
        }
        @if (sitesHeldBackText) {
        <div class="sh-muted mt-2 pm-sites-held">{{sitesHeldBackText}}</div>
        }
        @if (manage) {
        <div class="pm-add-site mt-3">
          <app-sb-input-text class="pm-add-site__input" label="Add site" placeholder="www.example.com"
                             [(ngModel)]="addHost" [error]="addHostError"
                             help="A hostname, approved straight away. A pasted address keeps only its host."></app-sb-input-text>
          <app-sb-button class="pm-add-site__button" data-action="add-site" variant="primary" size="md" label="Add site"
                         [disabled]="busy || !addHost" (clicked)="addSite()"></app-sb-button>
        </div>
        }
      </div>
    </div>

    <!-- 4. Issues -->
    <div class="card card-box mb-4 pm-issues">
      <div class="card-header py-3">
        <div class="card-header--title font-size-lg">Issues</div>
      </div>
      <div class="card-body">
        @if (!openErrors.length && !openWarnings.length) {
        <div class="sh-muted mb-3 pm-no-issues">
          {{acknowledgedIssues.length ? 'No unacknowledged issue.' : 'No open issue.'}}
        </div>
        }

        <ng-container *ngTemplateOutlet="issueGroup; context: {$implicit: openErrors, level: 'error', title: 'Errors'}"></ng-container>
        <ng-container *ngTemplateOutlet="issueGroup; context: {$implicit: openWarnings, level: 'warning', title: 'Warnings'}"></ng-container>

        @if (acknowledgedIssues.length) {
        <div class="pm-issue-group pm-acknowledged">
          <div class="pm-issue-group__head">
            <div class="pm-group-title">Acknowledged <span class="sh-muted">· {{acknowledgedIssues.length}}</span></div>
          </div>
          <p class="sh-muted mb-2">Still open and checked every day, never emailed. Each closes by itself once the problem is gone.</p>
          @for (issue of acknowledgedIssues; track trackById($index, issue)) {
          <div class="pm-issue pm-issue--acknowledged"
               [attr.data-issue-id]="issue.id" [class]="'pm-issue--' + issue.level">
            <div class="pm-issue__head">
              <span [class]="levelBadgeClass(issue.level)">{{levelLabel(issue.level)}}</span>
              <span class="pm-issue__title">{{issue.title}}</span>
              @if (issue.type === 'manual') {
              <span class="pm-chip pm-chip--manual">Manual</span>
              }
            </div>
            <div class="pm-issue__explanation">{{issue.explanation}}</div>
            @if (evidenceEntries(issue).length) {
            <div class="pm-kv">
              @for (entry of evidenceEntries(issue); track trackByKey($index, entry)) {
              <span><span class="pm-kv__key">{{entry.key}}</span> {{entry.value}}</span>
              }
            </div>
            }
            @if (reportedText(issue); as reported) {
            <div class="sh-muted pm-issue__reported">{{reported}}</div>
            }
            <div class="pm-issue__ack">{{acknowledgedText(issue)}}</div>
            @if (issue.note) {
            <div class="sh-muted pm-issue__note">Note: {{issue.note}}</div>
            }
            <div class="sh-muted">First seen {{time(issue.openedAt)}} · last seen {{time(issue.lastSeenAt)}}</div>
            @if (manage) {
            <div class="pm-actions mt-2">
              <app-sb-button data-action="ack-change" variant="outline-secondary" size="sm" icon="fa fa-edit" label="Change"
                             [disabled]="busy" (clicked)="changeAcknowledgement(issue)"></app-sb-button>
            </div>
            }
          </div>
          }
        </div>
        }

        @if (settledIssues.length) {
        <div class="pm-collapsible">
          <button type="button" class="pm-collapsible__toggle" (click)="showSettled = !showSettled" [attr.aria-expanded]="showSettled">
            <i class="fa" [class]="showSettled ? 'fa-chevron-down' : 'fa-chevron-right'" aria-hidden="true"></i>
            Closed ({{settledIssues.length}})
          </button>
          @if (showSettled) {
          <div class="pm-settled">
            @for (issue of settledIssues; track trackById($index, issue)) {
            <div class="pm-issue pm-issue--settled" [attr.data-issue-id]="issue.id">
              <div class="pm-issue__head">
                <span [class]="levelBadgeClass(issue.level)">{{levelLabel(issue.level)}}</span>
                <span class="pm-issue__title">{{issue.title}}</span>
                @if (issue.type === 'manual') {
                <span class="pm-chip pm-chip--manual">Manual</span>
                }
              </div>
              @if (reportedText(issue); as reported) {
              <div class="sh-muted pm-issue__reported">{{reported}}</div>
              }
              <div class="sh-muted pm-issue__state">{{issueStateText(issue)}}</div>
              @if (issue.note) {
              <div class="sh-muted">Note: {{issue.note}}</div>
              }
              <div class="sh-muted">First seen {{time(issue.openedAt)}} · last seen {{time(issue.lastSeenAt)}}</div>
            </div>
            }
          </div>
          }
        </div>
        }

        @if (data.checks?.length) {
        <div class="pm-collapsible">
          <button type="button" class="pm-collapsible__toggle" (click)="showChecks = !showChecks" [attr.aria-expanded]="showChecks">
            <i class="fa" [class]="showChecks ? 'fa-chevron-down' : 'fa-chevron-right'" aria-hidden="true"></i>
            What's checked
          </button>
          @if (unavailableChecks.length) {
          <div class="sh-alert sh-alert--unknown mt-2 pm-unavailable">
            {{unavailableChecks.length === 1 ? '1 check is' : unavailableChecks.length + ' checks are'}} unavailable:
            @for (check of unavailableChecks; track check; let last = $last) {<ng-container>{{check.title}}@if (check.unavailableReason) { ({{check.unavailableReason}})}{{last ? '.' : '; '}}</ng-container>}
          </div>
          }
          @if (showChecks) {
          <div class="pm-checks">
            @for (level of ['error', 'warning']; track level) {
              @if (checksOfLevel(level).length) {
              <div class="pm-checks__group">
                <div class="pm-group-title">{{level === 'error' ? 'Errors' : 'Warnings'}}</div>
                @for (check of checksOfLevel(level); track check) {
                <div class="pm-check" [class.pm-check--off]="!check.available" [attr.data-kind]="check.kind">
                  <div class="pm-check__title">
                    {{check.title}}
                    @if (!check.available) {
                    <span class="pm-chip">unavailable</span>
                    }
                    @if (check.mutedUntil) {
                    <span class="pm-chip pm-chip--muted">muted {{muteText(check.mutedUntil)}}</span>
                    }
                  </div>
                  <div class="sh-muted">{{check.description}}</div>
                  @if (thresholdEntries(check).length) {
                  <div class="pm-kv">
                    @for (entry of thresholdEntries(check); track trackByKey($index, entry)) {
                    <span><span class="pm-kv__key">{{entry.key}}</span> {{entry.value}}</span>
                    }
                  </div>
                  }
                  @if (!check.available && check.unavailableReason) {
                  <div class="sh-muted">{{check.unavailableReason}}</div>
                  }
                </div>
                }
              </div>
              }
            }
          </div>
          }
        </div>
        }
      </div>
    </div>

    <!-- 5. Metrics -->
    <div class="card card-box mb-4 pm-metrics">
      <div class="card-header py-3">
        <div class="card-header--title font-size-lg">Metrics</div>
      </div>
      <div class="card-body">
        @if (!data.metrics) {
          @if (data.monitoring.active) {
            @if (!data.monitoring.lastCheckedAt) {
            <div class="sh-muted pm-no-metrics">No check has run yet, so there are no metrics.</div>
            }
            @if (data.monitoring.lastCheckedAt) {
            <div class="sh-muted pm-no-metrics">No check has collected metrics yet.</div>
            }
          } @else {
          <div class="sh-muted pm-no-metrics">Monitoring is paused, so no metrics are collected.</div>
          }
          <ng-template #pausedMetrics><div class="sh-muted pm-no-metrics">Monitoring is paused, so no metrics are collected.</div></ng-template>
        }
        @if (data.metrics; as metrics) {
          <p class="sh-muted mb-3">Live traffic on external sites only, test traffic excluded. Measured {{time(metrics.checkedAt)}}; n/a means the metric is unavailable.</p>
          <div class="sh-table-wrap">
            <table class="sh-table pm-table pm-metrics__table">
              <thead>
              <tr>
                <th>Metric</th>
                <th class="pm-num">Last 24 hours</th>
                <th class="pm-num">Last 7 days</th>
                <th class="pm-num">Previous 7 days</th>
              </tr>
              </thead>
              <tbody>
              @for (row of metricRows; track trackByKey($index, row)) {
              <tr>
                <td>{{row.label}}</td>
                <td class="pm-num">{{metricText($safeNavigationMigration(metrics.windows.day?.[row.key]))}}</td>
                <td class="pm-num">{{metricText($safeNavigationMigration(metrics.windows.week?.[row.key]))}}</td>
                <td class="pm-num">{{metricText($safeNavigationMigration(metrics.windows.previousWeek?.[row.key]))}}</td>
              </tr>
              }
              @for (tracker of trackerKeys; track tracker) {
              <tr class="pm-subrow">
                <td>{{trackerRowLabel(tracker)}}</td>
                <td class="pm-num">{{metricText(trackerValue(metrics.windows.day, tracker))}}</td>
                <td class="pm-num">{{metricText(trackerValue(metrics.windows.week, tracker))}}</td>
                <td class="pm-num">{{metricText(trackerValue(metrics.windows.previousWeek, tracker))}}</td>
              </tr>
              }
              </tbody>
            </table>
          </div>

          <div class="pm-group-title mt-4 pm-loads-title">Widget loads by host@if (windowText(metrics.loadsByHostWindowHours); as window) { ({{window}})}</div>
          @if (!metrics.loadsByHost?.length) {
          <div class="sh-muted">No widget load on an external site in the latest check.</div>
          }
          @if (metrics.loadsByHost?.length) {
          <div class="sh-table-wrap">
            <table class="sh-table pm-table">
              <thead>
              <tr>
                <th>Host</th>
                <th class="pm-num">Loads</th>
                <th class="pm-num">Failed</th>
                <th>Expected</th>
                <th>Site</th>
              </tr>
              </thead>
              <tbody>
              @for (row of metrics.loadsByHost; track row) {
              <tr>
                <td class="pm-cell--wrap">{{row.host}}</td>
                <td class="pm-num">{{metricText(row.loads)}}</td>
                <td class="pm-num">{{metricText(row.failed)}}</td>
                <td>{{row.expected ? 'Yes' : 'No'}}</td>
                <td>@if (row.siteStatus) {
<span [class]="siteChipClass(row.siteStatus)">{{row.siteStatus}}</span>
              } @else {
              <span class="sh-muted">not listed</span>
              }
                  <ng-template #notASite><span class="sh-muted">not listed</span></ng-template></td>
              </tr>
          }
              </tbody>
            </table>
          </div>
        }
}
      </div>
    </div>

    <!-- 6. History -->
    <div class="card card-box mb-5 pm-history">
      <div class="card-header py-3">
        <div class="card-header--title font-size-lg">History</div>
      </div>
      <div class="card-body">
        <app-sb-tabs [activeId]="historyType" (activeIdChange)="onHistoryTab($event)">
          <ng-template sbTab="issues" title="Issues">
            <p class="sh-muted mt-3 mb-2">Every error and warning this project has had, newest first, with how each one was dealt with.</p>
            <div class="pm-history-filters">
              <app-sb-input-select class="pm-history-filters__field" label="Level" data-filter="level"
                                   [items]="issueLevelFilters" labelKey="name" valueKey="id" [clearable]="false"
                                   [(ngModel)]="issueLevelFilter" (ngModelChange)="onIssueFilterChange()"></app-sb-input-select>
              <app-sb-input-select class="pm-history-filters__field" label="State" data-filter="state"
                                   [items]="issueStateFilters" labelKey="name" valueKey="id" [clearable]="false"
                                   [(ngModel)]="issueStateFilter" (ngModelChange)="onIssueFilterChange()"></app-sb-input-select>
            </div>
            @if (historyError) {
            <div class="sh-alert sh-alert--error mt-3">{{historyError}}</div>
            }
            @if (historyLoading && !history) {
            <div class="sh-muted mt-3">Loading history…</div>
            }
            @if (history && !history.rows.length) {
            <div class="sh-muted mt-3 pm-no-issue-history">
              {{issueFiltered ? 'No issue matches these filters.' : 'No error or warning found yet.'}}
            </div>
            }
            @if (issueRows.length) {
            <div class="sh-table-wrap">
              <table class="sh-table pm-table pm-issue-history">
                <thead>
                <tr>
                  <th>Level</th>
                  <th>Issue</th>
                  <th>State</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                  <th class="pm-num">Times seen</th>
                  <th>Closed</th>
                  <th>Acknowledged</th>
                  <th>Muted until</th>
                </tr>
                </thead>
                <tbody>
                @for (row of issueRows; track trackById($index, row)) {
                <tr [attr.data-issue-id]="row.id" [attr.data-state]="row.state">
                  <td><span class="pm-history__level" [class]="levelBadgeClass(row.level)">{{levelLabel(row.level)}}</span></td>
                  <td class="pm-cell--wrap pm-history__issue">
                    <strong class="pm-history__title">{{row.title}}</strong>
                    @if (row.type === 'manual') {
                    <span class="pm-chip pm-chip--manual ms-1">Manual</span>
                    }
                    @if (reportedText(row); as reported) {
                    <div class="sh-muted pm-history__reported">{{reported}}</div>
                    }
                    @if (evidenceEntries(row).length) {
                    <div class="pm-kv pm-history__evidence">
                      @for (entry of evidenceEntries(row); track trackByKey($index, entry)) {
                      <span><span class="pm-kv__key">{{entry.key}}</span> {{entry.value}}</span>
                      }
                    </div>
                    }
                    @if (row.note) {
                    <div class="sh-muted pm-history__note">Note: {{row.note}}</div>
                    }
                  </td>
                  <td class="pm-history__state">{{issueStateLabel(row.state)}}</td>
                  <td>{{time(row.openedAt)}}</td>
                  <td>{{time(row.lastSeenAt)}}</td>
                  <td class="pm-num pm-history__seen">{{seenText(row.seenCount)}}</td>
                  <td class="pm-history__closed">
                    @if (row.closedAt) { {{time(row.closedAt)}}@if (row.closeReason) {
<div class="sh-muted">{{closeReasonLabel(row.closeReason)}}</div>
                    }
                } @else {
                <span class="sh-muted">—</span>
                }
                    <ng-template #notClosed><span class="sh-muted">—</span></ng-template>
                  </td>
                  <td class="pm-cell--wrap pm-history__ack">
                    @if (row.acknowledgedAt) { {{time(row.acknowledgedAt)}}@if (row.acknowledgedBy?.name) {
<div class="sh-muted">by {{row.acknowledgedBy.name}}</div>
                    }
            } @else {
            <span class="sh-muted">—</span>
            }
                    <ng-template #notAcknowledged><span class="sh-muted">—</span></ng-template>
                  </td>
                  <td class="pm-history__muted">{{mutedUntilText(row.mutedUntil)}}</td>
                </tr>
}
                </tbody>
              </table>
            </div>
}
            <ng-container *ngTemplateOutlet="historyPager"></ng-container>
          </ng-template>
          <ng-template sbTab="checks" title="Checks">
            <ng-container *ngTemplateOutlet="historyState"></ng-container>
            @if (checkRows.length) {
            <div class="sh-table-wrap">
              <table class="sh-table pm-table">
                <thead>
                <tr>
                  <th>Time</th>
                  <th>Trigger</th>
                  <th>Monitoring</th>
                  <th>Status</th>
                  <th class="pm-num">Issues</th>
                  <th class="pm-num">Duration</th>
                </tr>
                </thead>
                <tbody>
                @for (row of checkRows; track trackById($index, row)) {
                <tr>
                  <td>{{time(row.checkedAt)}}</td>
                  <td>{{row.trigger === 'manual' ? 'Run now' : 'Daily'}}@if (row.triggeredBy?.name) { by {{row.triggeredBy.name}}}</td>
                  <td>{{row.monitoringActive ? 'Active' : 'Paused'}}</td>
                  <td><span [class]="levelBadgeClass(row.status)">{{levelLabel(row.status)}}</span></td>
                  <td class="pm-num">{{metricText(row.issueCount)}}</td>
                  <td class="pm-num">{{durationText(row.durationMs)}}</td>
                </tr>
                }
                </tbody>
              </table>
            </div>
            }
            <ng-container *ngTemplateOutlet="historyPager"></ng-container>
          </ng-template>
          <ng-template sbTab="events" title="Events">
            <ng-container *ngTemplateOutlet="historyState"></ng-container>
            @if (eventRows.length) {
            <div class="sh-table-wrap">
              <table class="sh-table pm-table">
                <thead>
                <tr>
                  <th>Time</th>
                  <th>Who</th>
                  <th>What</th>
                </tr>
                </thead>
                <tbody>
                @for (row of eventRows; track trackById($index, row)) {
                <tr>
                  <td>{{time(row.occurredAt)}}</td>
                  <td>{{row.actor?.name || 'System'}}</td>
                  <td class="pm-cell--wrap">{{row.summary}}</td>
                </tr>
                }
                </tbody>
              </table>
            </div>
            }
            <ng-container *ngTemplateOutlet="historyPager"></ng-container>
          </ng-template>
        </app-sb-tabs>
      </div>
    </div>

}
</div>
</app-page-shell>

<ng-template #issueGroup let-issues let-level="level" let-title="title">
  @if (issues.length) {
  <div class="pm-issue-group" [attr.data-level]="level">
    <div class="pm-issue-group__head">
      <div class="pm-group-title">{{title}} <span class="sh-muted">· {{issues.length}}</span></div>
      @if (manage && issues.length > 1) {
      <app-sb-button [attr.data-action]="'ack-all-' + level" variant="outline-secondary" size="sm"
                     [label]="'Acknowledge all ' + (level === 'error' ? 'errors' : 'warnings')"
                     [disabled]="busy" (clicked)="acknowledgeAll(level)"></app-sb-button>
      }
    </div>
    @for (issue of issues; track trackById($index, issue)) {
    <div class="pm-issue" [attr.data-issue-id]="issue.id" [class]="'pm-issue--' + issue.level">
      <div class="pm-issue__head">
        <span [class]="levelBadgeClass(issue.level)">{{levelLabel(issue.level)}}</span>
        <span class="pm-issue__title">{{issue.title}}</span>
        @if (issue.type === 'manual') {
        <span class="pm-chip pm-chip--manual">Manual</span>
        }
      </div>
      <div class="pm-issue__explanation">{{issue.explanation}}</div>
      @if (evidenceEntries(issue).length) {
      <div class="pm-kv">
        @for (entry of evidenceEntries(issue); track trackByKey($index, entry)) {
        <span><span class="pm-kv__key">{{entry.key}}</span> {{entry.value}}</span>
        }
      </div>
      }
      @if (reportedText(issue); as reported) {
      <div class="sh-muted pm-issue__reported">{{reported}}</div>
      }
      <div class="sh-muted">First seen {{time(issue.openedAt)}} · last seen {{time(issue.lastSeenAt)}}</div>
      @if (manage) {
      <div class="pm-actions mt-2">
        @if (issue.confirmable) {
        <app-sb-button data-action="confirm-links" variant="outline" size="sm" icon="fa fa-check" label="Acknowledge: works as expected"
                       [disabled]="busy" (clicked)="confirmLinks(issue)"></app-sb-button>
        } @else {
        <app-sb-button data-action="acknowledge" variant="outline" size="sm" icon="fa fa-check" label="Acknowledge"
                       [disabled]="busy" (clicked)="acknowledge(issue)"></app-sb-button>
        }
      </div>
      }
    </div>
    }
  </div>
  }
</ng-template>

<ng-template #historyState>
  @if (historyError) {
  <div class="sh-alert sh-alert--error mt-3">{{historyError}}</div>
  }
  @if (historyLoading && !history) {
  <div class="sh-muted mt-3">Loading history…</div>
  }
  @if (history && !history.rows.length) {
  <div class="sh-muted mt-3">Nothing logged yet.</div>
  }
</ng-template>

<ng-template #historyPager>
  @if (history && history.total > history.rows.length) {
  <div class="pm-pager mt-3">
    <app-sb-button variant="outline-secondary" size="sm" icon="fa fa-chevron-left" label="Newer"
                   [disabled]="historyLoading || historyPage <= 1" (clicked)="historyGo(-1)"></app-sb-button>
    <span class="sh-muted">Page {{historyPage}} of {{historyTotalPages}} · {{history.total}} in all</span>
    <app-sb-button variant="outline-secondary" size="sm" icon="fa fa-chevron-right" iconPosition="end" label="Older"
                   [disabled]="historyLoading || historyPage >= historyTotalPages" (clicked)="historyGo(1)"></app-sb-button>
  </div>
  }
</ng-template>

<ng-template #ackFormTpl>
  <div class="pm-dialog">
    @if (ackTarget.level) {
    <p class="mb-3 pm-dialog__scope">Applies to every unacknowledged open {{ackTarget.level === 'error' ? 'error' : 'warning'}}; acknowledged ones keep their choice.</p>
    }
    <app-sb-input-radio class="d-block mb-3 pm-dialog__choice" ariaLabel="What to do with the issue" [options]="ackOptions"
                        [(ngModel)]="ackForm.resolution"></app-sb-input-radio>
    @if (muteOffered) {
    <app-sb-input-select class="d-block mb-3 pm-dialog__mute" label="Mute this check for"
                         [items]="muteOptions" labelKey="name" valueKey="id" [clearable]="false"
                         help="Default period is the one set for monitoring."
                         [(ngModel)]="ackForm.mute"></app-sb-input-select>
    }
    <app-sb-input-textarea class="pm-dialog__note" label="Note" description="What was done, or why" [rows]="3"
                           [(ngModel)]="ackForm.note"></app-sb-input-textarea>
  </div>
</ng-template>

<ng-template #logIssueTpl>
  <div class="pm-dialog pm-log-dialog">
    <p class="mb-3 sh-muted">Something the checks did not raise. It is listed with the open issues and kept in History &gt; Issues
      with your name, to fine-tune the checks.</p>
    <app-sb-input-radio class="d-block mb-3 pm-log-dialog__level" ariaLabel="Level" [options]="logLevelOptions"
                        [(ngModel)]="logForm.level"></app-sb-input-radio>
    <app-sb-input-textarea class="pm-log-dialog__description" label="What did you see?" [required]="true" [rows]="5"
                           description="Where, when, and what was expected instead"
                           [ngModel]="logForm.description" (ngModelChange)="onLogDescriptionChange($event)"></app-sb-input-textarea>
  </div>
</ng-template>

<ng-template #disableTpl>
  <div class="pm-dialog pm-disable-dialog">
    <p class="mb-3 sh-muted">Nothing is checked or emailed for this project until someone enables monitoring again,
      whatever its dates. The issues a check raised are closed; the ones logged by hand stay open.</p>
    <app-sb-input-textarea class="pm-disable-dialog__note" label="Why?" [rows]="3"
                           description="Optional: a test project, a demo, ..."
                           [(ngModel)]="disableForm.note"></app-sb-input-textarea>
  </div>
</ng-template>

<ng-template #siteFormTpl>
  <div class="pm-dialog">
    @if (siteForm.action === 'approve') {
    <p class="mb-3">{{siteForm.site?.host}} becomes an expected site.</p>
    }
    @if (siteForm.action === 'decline') {
    <p class="mb-3">{{siteForm.site?.host}} is no longer expected: widget loads there count as a wrong page.</p>
    }
    @if (siteForm.action === 'replace') {
      <p class="mb-3">{{siteForm.site?.host}} is declined and the host you type is approved in its place.</p>
      <app-sb-input-text class="d-block mb-3" label="New host" placeholder="www.example.com" [required]="true"
                         [error]="siteForm.hostError" [ngModel]="siteForm.host" (ngModelChange)="onReplaceHostChange($event)"></app-sb-input-text>
    }
    <app-sb-input-textarea label="Note (optional)" [rows]="3" [(ngModel)]="siteForm.note"></app-sb-input-textarea>
  </div>
</ng-template>
